/**
 * SendEmailModal Component
 * Modal for composing and sending an email to a contact — either a plain-text message OR a chosen
 * SendGrid dynamic template (with first/last-name merge). Sends via the server-owned
 * `send-contact-quick-email` edge function (role-gated, recipient loaded server-side from contact_id,
 * canonical suppression honored, idempotent). Reports honest outcomes — "accepted for sending",
 * suppressed, or not sent — never a false success.
 *
 * Template sends: the server RE-SOURCES merge values from the loaded contact (we only tell it which
 * template + which fields the template declares), so the browser can't inject arbitrary merge content.
 * A chosen template is also saved to email_template_registry so it is quick-pickable for all contacts.
 */

import React, { useEffect, useRef, useState } from "react";
import Modal from "../common/Modal";
import { useToast } from "../ui/Toast";
import { supabase } from "../../services/supabase";
import SendGridTemplateModal from "../campaigns/SendGridTemplateModal";
import {
  getSendGridTemplateDetails,
  listTemplateRegistry,
  removeTemplateFromRegistry,
  isSelfReferentialSubject,
  extractOverridableSubjectFallback,
  quickEmailUnsupportedMergeFields,
  type SendGridTemplateDetails,
  type EmailTemplateRegistryRecord,
} from "../../services/templates.service";
import type { Contact } from "../../types/contact.types";

interface SendEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  contact: Contact;
}

// Common email templates (plain-text quick messages — distinct from SendGrid templates).
const EMAIL_TEMPLATES = [
  {
    label: "Follow Up",
    subject: "Following Up - US Law Shield",
    body: `Hi {{name}},

I wanted to follow up regarding your US Law Shield membership. Please don't hesitate to reach out if you have any questions or concerns.

Best regards,
US Law Shield Support Team`,
  },
  {
    label: "Payment Reminder",
    subject: "Payment Reminder - US Law Shield",
    body: `Hi {{name}},

We noticed that your recent payment for your US Law Shield membership was unsuccessful. To ensure your coverage remains active, please update your payment information at your earliest convenience.

If you have any questions or need assistance, please don't hesitate to contact us.

Best regards,
US Law Shield Support Team`,
  },
  {
    label: "Thank You",
    subject: "Thank You for Being a Member - US Law Shield",
    body: `Hi {{name}},

Thank you for being a valued US Law Shield member. We truly appreciate your trust in us and are committed to providing you with the best legal protection coverage.

If there's anything we can do to better serve you, please let us know.

Best regards,
US Law Shield Support Team`,
  },
];

/**
 * Which SendGrid merge fields the SERVER will personalize from the contact. Mirrors the allowlist in
 * supabase/functions/send-contact-quick-email/logic.ts (buildContactTemplateData) — kept in sync so
 * the modal's "Personalizes:" hint matches what actually gets sent. Match is case-insensitive; the
 * server emits the value under the exact declared key.
 */
const PERSONALIZABLE_CANONICAL: Record<string, string> = {
  firstname: "First name",
  fname: "First name",
  lastname: "Last name",
  lname: "Last name",
  fullname: "Full name",
  name: "Full name",
  email: "Email",
  emailaddress: "Email",
};

function personalizableFields(mergeFields: string[]): string[] {
  const out: string[] = [];
  for (const f of mergeFields) {
    const canonical = f.toLowerCase().replace(/[^a-z0-9]/g, "");
    if (PERSONALIZABLE_CANONICAL[canonical]) {
      out.push(f);
    }
  }
  return out;
}

const SendEmailModal: React.FC<SendEmailModalProps> = ({
  isOpen,
  onClose,
  contact,
}) => {
  const { showToast } = useToast();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [isSending, setIsSending] = useState(false);

  // SendGrid template selection. When set, the send uses the dynamic template instead of plain text.
  const [selectedTemplate, setSelectedTemplate] =
    useState<SendGridTemplateDetails | null>(null);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [savedTemplates, setSavedTemplates] = useState<
    EmailTemplateRegistryRecord[]
  >([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  // Remove-a-saved-template confirmation flow (Feature 2, remove path).
  const [templateToRemove, setTemplateToRemove] =
    useState<EmailTemplateRegistryRecord | null>(null);
  const [removing, setRemoving] = useState(false);

  // HIGH-1: the previous attempt was AMBIGUOUS (may have been sent). We keep the SAME request id and
  // require an explicit "Send again anyway" click before minting a new id and resending, so an
  // unconfirmed send can never be silently duplicated.
  const [ambiguous, setAmbiguous] = useState(false);
  // Stable per-ATTEMPT id: shared by any rapid re-clicks of the same attempt so the server dedupes
  // them to one provider submission (not relying on the UI disable). Regenerated ONLY after a
  // definitively-resolved outcome (accepted/suppressed/failed/invalid) — never after ambiguous/unknown.
  const requestIdRef = useRef<string>(crypto.randomUUID());

  const contactName = contact.first_name;

  // Load saved (previously-used) templates whenever the modal opens.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    let cancelled = false;
    setLoadingSaved(true);
    listTemplateRegistry(50)
      .then((rows) => {
        if (!cancelled) {
          setSavedTemplates(rows);
        }
      })
      .catch(() => {
        /* non-fatal — the live search still works */
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingSaved(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Reload the shared saved-templates list (after a save or a remove).
  const refreshSaved = () => {
    listTemplateRegistry(50)
      .then((rows) => setSavedTemplates(rows))
      .catch(() => {
        /* non-fatal — the live browser still works */
      });
  };

  const handleConfirmRemove = async () => {
    if (!templateToRemove) {
      return;
    }
    const removed = templateToRemove;
    setRemoving(true);
    try {
      await removeTemplateFromRegistry(removed.sendgrid_template_id);
      showToast("success", "Removed from saved templates.");
      setTemplateToRemove(null);
      // Optimistically drop the chip so the list shrinks immediately, then reconcile with the server.
      setSavedTemplates((prev) => prev.filter((t) => t.id !== removed.id));
      refreshSaved();
    } catch {
      showToast(
        "error",
        "Couldn't remove that saved template. You may not have permission, or it may already be removed.",
      );
    } finally {
      setRemoving(false);
    }
  };

  const handleSelectPlainTemplate = (template: {
    subject: string;
    body: string;
  }) => {
    // Choosing a plain-text quick message clears any SendGrid template selection.
    setSelectedTemplate(null);
    const personalizedBody = template.body.replaceAll(
      "{{name}}",
      contactName || "there",
    );
    setSubject(template.subject);
    setBody(personalizedBody);
  };

  // Apply a chosen SendGrid template (from the live picker OR a saved quick-pick).
  // Returns true if the template was applied, false if it was blocked (unsupported
  // merge fields) so callers can keep the picker open for another choice.
  const applySendGridTemplate = (details: SendGridTemplateDetails): boolean => {
    // Guard: a quick email only fills first_name/last_name/email (plus SendGrid's own
    // auto-injected tokens). A template that declares any other merge field would send
    // those as empty strings — block the selection and tell the operator why.
    const unsupported = quickEmailUnsupportedMergeFields(
      details.merge_fields || [],
    );
    if (unsupported.length > 0) {
      showToast(
        "error",
        `This template can't be used for a quick email — it needs merge fields we don't fill here: ${unsupported.join(", ")}. Quick emails only support first name, last name, and email.`,
      );
      return false;
    }
    setSelectedTemplate(details);
    // Prefill the subject from the template's own subject. The field is then locked (the
    // subject is owned by the template). A BARE `{{subject}}` / `{{{subject}}}` self-reference
    // resolves to itself with no default — leave blank and keep the field editable so the
    // operator supplies a real one. But SendGrid's overridable idiom
    // `{{#if subject}}{{subject}}{{else}}Default{{/if}}` carries a default in its `{{else}}`
    // branch — pre-fill that (so Send isn't blocked) instead of blanking.
    const rawSubject = details.version?.subject;
    const derivedSubject = extractOverridableSubjectFallback(rawSubject);
    if (derivedSubject !== null) {
      // A conditional subject ({{#if subject}}…{{else}}…, or {{#if first_name}}…) →
      // use the derived default / merge-field branch.
      setSubject(derivedSubject);
    } else if (!isSelfReferentialSubject(rawSubject)) {
      // A plain subject (possibly with simple {{first_name}} tokens) → use it as-is.
      setSubject(rawSubject || "");
    } else {
      // A BARE {{subject}} / {{{subject}}} with no default → operator must supply one.
      setSubject("");
    }
    // Selecting a template does NOT save it to the contact's saved list. Saving is an EXPLICIT
    // action — the star in the template picker (enableSaveToSavedList → onSavedToList) writes
    // saved_for_contact_email=true. Previously this auto-saved every picked template, so the saved
    // list filled up with templates the operator only wanted to send once.
    return true;
  };

  const handlePickFromLive = (details: SendGridTemplateDetails) => {
    if (applySendGridTemplate(details)) {
      setShowTemplatePicker(false);
    }
  };

  const handlePickSaved = async (row: EmailTemplateRegistryRecord) => {
    try {
      // Fetch fresh details (subject/preview/merge fields) for the saved SendGrid template id.
      const details = await getSendGridTemplateDetails(
        row.sendgrid_template_id,
      );
      applySendGridTemplate(details);
    } catch {
      showToast(
        "error",
        "Couldn't load that saved template. It may have been removed in SendGrid.",
      );
    }
  };

  const clearTemplate = () => {
    setSelectedTemplate(null);
  };

  const usingTemplate = selectedTemplate !== null;
  // A BARE `{{subject}}` template intentionally has no fixed subject — the operator must supply
  // one, so keep the field editable. The overridable idiom carries a default in its `{{else}}`
  // branch (pre-filled above), so it does NOT need a manual subject. Every other template owns
  // its subject → lock the field.
  const templateNeedsManualSubject =
    usingTemplate &&
    isSelfReferentialSubject(selectedTemplate!.version?.subject) &&
    extractOverridableSubjectFallback(selectedTemplate!.version?.subject) ===
      null;
  const subjectLocked = usingTemplate && !templateNeedsManualSubject;
  const personalized = usingTemplate
    ? personalizableFields(selectedTemplate!.merge_fields || [])
    : [];

  const handleSend = async () => {
    if (!subject.trim()) {
      showToast("error", "Please enter a subject");
      return;
    }
    if (!usingTemplate && !body.trim()) {
      showToast("error", "Please enter a message (or choose a template)");
      return;
    }
    if (!contact.email) {
      showToast("error", "This contact has no email address on file.");
      return;
    }

    // HIGH-1: if the last attempt was ambiguous, THIS click is the explicit "Send again anyway" — the
    // acknowledged decision to accept a possible duplicate, so mint a fresh id. Otherwise keep the
    // stable id (rapid re-clicks of the same attempt dedupe server-side).
    if (ambiguous) {
      requestIdRef.current = crypto.randomUUID();
      setAmbiguous(false);
    }
    setIsSending(true);
    const clientRequestId = requestIdRef.current;

    // Template branch: send the template id + the fields it declares. The SERVER re-sources the merge
    // values from the contact — we send field names only (values are placeholders, ignored server-side).
    const templatePayload = usingTemplate
      ? {
          template_id: selectedTemplate!.id,
          template_data: Object.fromEntries(
            (selectedTemplate!.merge_fields || []).map((f) => [f, ""]),
          ),
        }
      : {};

    try {
      const { data, error } = await supabase.functions.invoke(
        "send-contact-quick-email",
        {
          body: {
            contact_id: contact.id,
            subject: subject.trim(),
            // Body is only meaningful for a plain-text send; harmless (ignored) for a template send.
            body: usingTemplate ? "" : body,
            client_request_id: clientRequestId,
            ...templatePayload,
          },
        },
      );

      // Network / unknown outcome (data null or invoke error): the request MAY have reached the server
      // and sent. Treat as AMBIGUOUS — keep the SAME id, keep the draft, and require an explicit resend.
      // Never a success, never a silent new-key retry.
      if (error || !data) {
        setAmbiguous(true);
        showToast(
          "error",
          "We couldn't confirm whether this email was sent. Check before resending — press Send again anyway to try once more.",
        );
        return;
      }

      const status = (
        data as { status?: string; message?: string; reason?: string }
      ).status;
      const message = (data as { message?: string }).message;

      if (status === "accepted") {
        showToast("success", message || "Email accepted for sending");
        requestIdRef.current = crypto.randomUUID(); // next send is a fresh, distinct attempt
        setSubject("");
        setBody("");
        setSelectedTemplate(null);
        onClose();
      } else if (status === "ambiguous") {
        // May have been sent — do NOT mint a new id or auto-resend. Require an explicit "Send again anyway".
        setAmbiguous(true);
        showToast(
          "error",
          message || "This email may have been sent. Check before resending.",
        );
      } else {
        // suppressed | failed | invalid — definitively NOT sent (or never reached the provider). Safe to
        // retry as a fresh attempt, so mint a new id.
        requestIdRef.current = crypto.randomUUID();
        showToast("error", message || "Email was not sent.");
      }
    } catch {
      // Thrown around the invoke — treat as ambiguous (unknown), never a definitive failure.
      setAmbiguous(true);
      showToast(
        "error",
        "We couldn't confirm whether this email was sent. Check before resending.",
      );
    } finally {
      setIsSending(false);
      // NOTE (HIGH-1): the request id is intentionally NOT regenerated here — only on a
      // definitively-resolved outcome above, never after an ambiguous/unknown one.
    }
  };

  const canSend =
    !!subject.trim() &&
    (usingTemplate || !!body.trim()) &&
    !!contact.email &&
    !isSending;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Send Email" size="lg">
      <div className="space-y-4">
        {/* Recipient info */}
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-sm text-gray-500">To:</div>
          <div className="font-medium text-gray-900">
            {contact.first_name} {contact.last_name}
          </div>
          <div className="text-sm text-gray-600">
            {contact.email || "No email address"}
          </div>
        </div>

        {/* SendGrid template chooser */}
        <div className="border border-gray-200 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">
              Send a designed email (SendGrid template)
            </label>
            {/* Back-arrow: always returns to the FULL SendGrid template browser, even when a template
                is already selected (Feature 3 — no dead-end at the saved list). */}
            <button
              type="button"
              onClick={() => setShowTemplatePicker(true)}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 rounded-md hover:bg-primary-100"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              {usingTemplate ? "Browse all templates" : "Choose template…"}
            </button>
          </div>

          {/* Saved (previously-used) templates quick-pick — shown in BOTH states so the operator can
              switch templates without first clearing the current one (Feature 3). Each is a REMOVABLE
              chip: the name picks it; the × removes it from the shared saved list (Feature 2). */}
          <div className="mt-2">
            <div className="text-xs text-gray-500 mb-1">
              {loadingSaved
                ? "Loading saved templates…"
                : savedTemplates.length > 0
                  ? "Saved templates"
                  : "No saved templates yet — choose one to save it for reuse."}
            </div>
            {savedTemplates.length > 0 && (
              <div className="flex flex-wrap gap-2 max-h-28 overflow-y-auto pr-1">
                {savedTemplates.map((t) => {
                  const isCurrent =
                    selectedTemplate?.id === t.sendgrid_template_id;
                  return (
                    <span
                      key={t.id}
                      className={`inline-flex items-center rounded-full text-xs font-medium max-w-[240px] ${
                        isCurrent
                          ? "bg-indigo-100 text-indigo-800"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => handlePickSaved(t)}
                        title={t.name}
                        className="pl-2.5 pr-1 py-1 truncate hover:underline"
                      >
                        {t.name}
                      </button>
                      <button
                        type="button"
                        onClick={() => setTemplateToRemove(t)}
                        title="Remove from saved templates"
                        aria-label={`Remove ${t.name} from saved templates`}
                        className="pl-0.5 pr-2 py-1 text-gray-400 hover:text-red-600"
                      >
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {/* Selected template summary */}
          {usingTemplate && selectedTemplate && (
            <div className="mt-3 rounded-md bg-indigo-50 border border-indigo-100 p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-indigo-900 truncate">
                    {selectedTemplate.name}
                  </div>
                  <div className="mt-1 text-xs text-indigo-700">
                    {personalized.length > 0 ? (
                      <>
                        Personalizes from this contact:{" "}
                        {personalized.join(", ")}
                      </>
                    ) : (
                      <>This template has no personalized name fields.</>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={clearTemplate}
                  className="flex-shrink-0 text-xs font-medium text-indigo-700 hover:text-indigo-900 underline"
                >
                  Write plain text instead
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Plain-text quick messages — only when NOT sending a SendGrid template */}
        {!usingTemplate && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quick Templates
            </label>
            <div className="flex flex-wrap gap-2">
              {EMAIL_TEMPLATES.map((template) => (
                <button
                  key={template.label}
                  onClick={() => handleSelectPlainTemplate(template)}
                  className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200"
                >
                  {template.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Subject input (shared by both modes) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Subject
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            readOnly={subjectLocked}
            placeholder={
              templateNeedsManualSubject
                ? "Enter a subject for this template..."
                : "Email subject..."
            }
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              subjectLocked
                ? "bg-gray-100 text-gray-600 border-gray-200 cursor-not-allowed"
                : "border-gray-300"
            }`}
          />
          {subjectLocked && (
            <p className="mt-1 text-xs text-gray-500">
              Set by the selected template. Clear the template to edit the
              subject.
            </p>
          )}
          {templateNeedsManualSubject && (
            <p className="mt-1 text-xs text-amber-600">
              This template needs a subject — enter one above.
            </p>
          )}
        </div>

        {/* Body input (plain-text mode) OR template notice */}
        {usingTemplate ? (
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-sm text-indigo-800">
            This email uses the selected SendGrid template. The template's own
            content will be sent; the contact's name is merged in automatically.
            {subjectLocked
              ? " The subject is set by the template."
              : " Enter a subject above for this template."}
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Type your message..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              rows={8}
            />
          </div>
        )}

        {/* Info notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <svg
              className="w-5 h-5 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="text-sm text-blue-800">
              Sent from U.S. LawShield to this contact's email on file. Contacts
              who have unsubscribed or bounced won't receive it.
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={!canSend}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSending
              ? "Sending..."
              : ambiguous
                ? "Send again anyway"
                : "Send Email"}
          </button>
        </div>
      </div>

      {/* SendGrid template picker (searchable autocomplete + preview + merge-field chips). The star on
          each row saves it to the shared saved list; onSavedToList refreshes the chips above. */}
      <SendGridTemplateModal
        isOpen={showTemplatePicker}
        onClose={() => setShowTemplatePicker(false)}
        onSelect={handlePickFromLive}
        selectedTemplateId={selectedTemplate?.id}
        enableSaveToSavedList
        onSavedToList={refreshSaved}
      />

      {/* Confirm removing a template from the SHARED saved list (Feature 2, remove path). */}
      <Modal
        isOpen={!!templateToRemove}
        onClose={() => (removing ? undefined : setTemplateToRemove(null))}
        title="Remove saved template"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            Remove <span className="font-medium">{templateToRemove?.name}</span>{" "}
            as a saved template for all users? It will no longer appear in the
            saved templates list. You can add it back later by choosing it from
            the full template browser.
          </p>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setTemplateToRemove(null)}
              disabled={removing}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmRemove}
              disabled={removing}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {removing ? "Removing…" : "Remove"}
            </button>
          </div>
        </div>
      </Modal>
    </Modal>
  );
};

export default SendEmailModal;
